import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, TextField } from "@mui/material"
import { type FC } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { type CompanyLegalInfoFormValues, CompanyLegalInfoSchema } from "../schemas/companyLegalInfo"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { SelectField } from "@/components/common/SelectField"
import { FormField } from "@/components/Forms"
import { type Company } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { NotificationType } from "@/schemas/notification"
import { useCreateNewCompanyModal } from "@/screens/Companies/hooks"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import { AddressFormat } from "@/types/index"
import { formatLocationAddress } from "@/utils/utils"

interface LegalInformationDialogProps {
  companyId: string
  legalInfo: Pick<Company, "legalName" | "legalLocation">
  onClose: () => void
  onSubmit: () => void
}

export const LegalInformationDialog: FC<LegalInformationDialogProps> = ({
  legalInfo,
  companyId,
  onClose,
  onSubmit,
}) => {
  const { enqueueNotification } = useNotificationsStore()

  const { updateCompany } = useCreateNewCompanyModal()

  const { locations } = useGetLocations(companyId)

  const methods = useForm({
    defaultValues: {
      legalName: legalInfo.legalName ?? "",
      legalLocation: legalInfo.legalLocation?.locationId ?? "",
    },
    resolver: zodResolver(CompanyLegalInfoSchema),
    mode: "all",
  })

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
  } = methods

  const onHandleSubmit = async (data: CompanyLegalInfoFormValues) => {
    const { error } = await updateCompany({
      input: { ...data, companyId },
    })

    if (error) {
      enqueueNotification({
        type: NotificationType.Error,
        message: "Failed to update legal information",
      })
      return
    }

    enqueueNotification({
      type: NotificationType.Success,
      message: "Company legal information successfully updated",
    })

    onSubmit()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog open onClose={handleClose} fullWidth>
      <DialogHeader title='Edit Legal Information' onClose={handleClose} />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <FormField
            name='legalName'
            label='Company Legal Name'
            required
            render={({ field }) => <TextField {...field} data-testid='legal-name-input' />}
          />

          <FormField
            name='legalLocation'
            label='Legal Address Location'
            required
            render={({ field, fieldState }) => (
              <SelectField
                field={field}
                fieldState={fieldState}
                options={locations.map((location) => ({
                  value: location.locationId,
                  label: formatLocationAddress(location, AddressFormat.LocationAndAddress),
                }))}
              />
            )}
          />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onHandleSubmit)} disabled={!isValid || !isDirty}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
